import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api";
import { normalizeSystemStatus } from "../lib/systemStatus";
import type { IntegrationStatusResponse, StreamStatus, SystemStatusResponse } from "../lib/types";
import { useDomainVersion } from "./invalidation";

type UseSystemStatusSummaryResult = {
  errors: string[];
  integrationStatus: IntegrationStatusResponse | null;
  normalizedSystemStatus: string | null;
  staleStreams: StreamStatus[];
  systemStatus: SystemStatusResponse | null;
};

function formatStatusLoadError(label: string, error: unknown): string {
  const message = error instanceof Error ? error.message : "Unknown error";
  return `${label}: ${message}`;
}

export function useSystemStatusSummary(): UseSystemStatusSummaryResult {
  const systemVersion = useDomainVersion("system");
  const [errors, setErrors] = useState<string[]>([]);
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatusResponse | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatusResponse | null>(null);
  const statusSummaryRequestSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++statusSummaryRequestSeqRef.current;

    async function loadStatusSummary() {
      setErrors([]);
      const [integrationResult, systemResult] = await Promise.allSettled([
        apiFetch<IntegrationStatusResponse>("/integrations/status"),
        apiFetch<SystemStatusResponse>("/system/status"),
      ]);
      if (cancelled || requestId !== statusSummaryRequestSeqRef.current) return;

      const nextErrors: string[] = [];
      if (integrationResult.status === "fulfilled") {
        setIntegrationStatus(integrationResult.value);
      } else {
        nextErrors.push(formatStatusLoadError("Sync stream status unavailable", integrationResult.reason));
      }

      if (systemResult.status === "fulfilled") {
        setSystemStatus(systemResult.value);
      } else {
        setSystemStatus(null);
        nextErrors.push(formatStatusLoadError("System status unavailable", systemResult.reason));
      }

      setErrors(nextErrors);
    }

    void loadStatusSummary();
    return () => {
      cancelled = true;
    };
  }, [systemVersion]);

  const staleStreams = integrationStatus?.streams.filter((stream) => stream.is_stale) ?? [];
  const normalizedSystemStatus = normalizeSystemStatus(systemStatus?.status);

  return {
    errors,
    integrationStatus,
    normalizedSystemStatus,
    staleStreams,
    systemStatus,
  };
}
